/**
 * CLARTEXT SPRACHE V1 – Phase 2: Lokale i18n-Struktur NUR für die Mitarbeiter-App.
 *
 * Bewusst KEIN i18n-Framework: eine kleine Dictionary-Map + `t(key, vars)` reicht
 * für den abgegrenzten Mitarbeiter-Bereich und hält den Blast-Radius minimal.
 *
 * Sprachauflösung:
 *   - greift NUR, wenn das serverseitige Feature-Flag aktiv ist
 *     (`employee.multilanguage_enabled`, exponiert über die Mitarbeiter-Session).
 *   - In Phase 2 ist das Flag AUS → IMMER Deutsch (verhaltens-/sichtbar identisch).
 *   - Sprache kommt aus `employee.preferred_language` (vom Unternehmer gesetzt),
 *     KEINE Browser-/Auto-Erkennung. Fallback immer "de".
 *
 * Owner-UI ist hiervon NICHT betroffen (eigener, weiterhin deutscher Bereich).
 */
import { useEmployeeSession } from "@/hooks/useEmployeeSession";
import de from "./de";
import en from "./en";
import pl from "./pl";
import ro from "./ro";
import uk from "./uk";
import bg from "./bg";
import tr from "./tr";
import hr from "./hr";
import ru from "./ru";

export const DEFAULT_MONTEUR_LANG = "de";
export const MONTEUR_DICTS = { de, en, pl, ro, uk, bg, tr, hr, ru };

export function resolveMonteurLang(employee) {
  if (!employee?.multilanguage_enabled) return DEFAULT_MONTEUR_LANG;
  const lang = employee?.preferred_language;
  return MONTEUR_DICTS[lang] ? lang : DEFAULT_MONTEUR_LANG;
}

export function createT(lang) {
  const dict = MONTEUR_DICTS[lang] || MONTEUR_DICTS[DEFAULT_MONTEUR_LANG];
  const fallback = MONTEUR_DICTS[DEFAULT_MONTEUR_LANG];
  return function t(key, vars) {
    let str = (key in dict ? dict[key] : fallback[key]) ?? key;
    if (vars) {
      for (const k of Object.keys(vars)) {
        str = str.split(`{${k}}`).join(String(vars[k]));
      }
    }
    return str;
  };
}

export function useMonteurT() {
  const { session } = useEmployeeSession();
  const lang = resolveMonteurLang(session?.employee);
  return createT(lang);
}

/**
 * Explizit erzwingbarer Übersetzungs-Hook für GETEILTE VLO-Renderer.
 * Wird `forcedLang` gesetzt (z. B. Owner-Kontext: "de"), gilt diese Sprache
 * unabhängig von der Mitarbeiter-Session. Ohne `forcedLang` = bisheriges
 * Verhalten (Sprache aus der Mitarbeiter-Session). Verhindert, dass eine im
 * selben Browser vorhandene Monteur-Session die Owner-/Unternehmer-UI übersetzt.
 */
export function useResolvedT(forcedLang) {
  const sessionT = useMonteurT();
  return forcedLang ? createT(forcedLang) : sessionT;
}

/**
 * CLARTEXT SPRACHE V1.1 (Demo-Feinschliff) – reiner ANZEIGE-Lookup für die festen
 * deutschen Abschnitts-Überschriften der Unterweisungs-Inhalte (`content[].heading`).
 *
 * Display-only: übersetzt AUSSCHLIESSLICH die in de.js/pl.js hinterlegten, exakt
 * bekannten Überschriften. Unbekannte Überschrift → unverändert zurückgegeben
 * (Fallback). KEINE freie/KI-Übersetzung, KEINE Datenmutation. Die in der DB
 * gespeicherten Inhalte + die Beweiskette bleiben deutsch/unverändert.
 */
export function translateContentHeading(t, heading) {
  if (!heading) return heading;
  const key = `contentHeading.${heading}`;
  const translated = t(key);
  // createT gibt bei unbekanntem Key den Key selbst zurück → dann Original behalten.
  return translated === key ? heading : translated;
}
